import React, { useState, useRef, useCallback } from 'react';
import { Check, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

/**
 * SpellCheckTextarea - Enhanced textarea with browser spellcheck and optional grammar checking
 * 
 * Features:
 * - Browser native spellcheck (real-time, zero latency)
 * - Optional grammar check button (on-demand)
 * - Inline error highlighting
 * - No impact on typing performance
 */
export function SpellCheckTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
  className = '',
  showGrammarCheck = true,
  label,
  ...props
}) {
  const [isChecking, setIsChecking] = useState(false);
  const [grammarErrors, setGrammarErrors] = useState([]);
  const [lastChecked, setLastChecked] = useState(null);
  const textareaRef = useRef(null);

  // Grammar check using LanguageTool API (free, no API key needed for basic use)
  const checkGrammar = useCallback(async () => {
    if (!value || value.trim().length < 3) {
      setGrammarErrors([]);
      return;
    }

    setIsChecking(true);
    try {
      const response = await fetch('https://api.languagetool.org/v2/check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          text: value,
          language: 'en-US',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setGrammarErrors(data.matches || []);
        setLastChecked(new Date());
      }
    } catch (error) {
      console.error('Grammar check failed:', error);
    } finally {
      setIsChecking(false);
    }
  }, [value]);

  // Apply suggestion to text
  const applySuggestion = (error, suggestion) => {
    const before = value.substring(0, error.offset);
    const after = value.substring(error.offset + error.length);
    const newValue = before + suggestion + after;
    onChange({ target: { value: newValue } });
    
    // Remove this error from the list
    setGrammarErrors(prev => prev.filter(e => e.offset !== error.offset));
  };

  // Get error type color
  const getErrorColor = (type) => {
    switch (type) {
      case 'misspelling':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'grammar':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'typographical':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-semibold text-[#1a1a2e]">{label}</label>
      )}
      
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={rows}
          spellCheck={true}
          autoCorrect="on"
          autoCapitalize="sentences"
          className={`w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${className}`}
          {...props}
        />
        
        {/* Grammar Check Button */}
        {showGrammarCheck && value && value.length > 10 && (
          <div className="absolute bottom-2 right-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={checkGrammar}
                    disabled={isChecking}
                    className="h-8 px-2 text-neutral-500 hover:text-pink-600 hover:bg-pink-50"
                  >
                    {isChecking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : grammarErrors.length > 0 ? (
                      <AlertCircle className="w-4 h-4 text-orange-500" />
                    ) : lastChecked ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    <span className="ml-1 text-xs">
                      {isChecking ? 'Checking...' : 
                       grammarErrors.length > 0 ? `${grammarErrors.length} issues` :
                       lastChecked ? 'Good!' : 'Check'}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Check spelling & grammar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>

      {/* Grammar Errors Display */}
      {grammarErrors.length > 0 && (
        <div className="space-y-2 mt-2">
          <p className="text-xs font-medium text-neutral-500">
            {grammarErrors.length} suggestion{grammarErrors.length > 1 ? 's' : ''} found:
          </p>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {grammarErrors.slice(0, 5).map((error, index) => (
              <div 
                key={index} 
                className={`p-2 rounded-xl border text-sm ${getErrorColor(error.rule?.issueType)}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      &quot;{value.substring(error.offset, error.offset + error.length)}&quot;
                    </p>
                    <p className="text-xs opacity-80 mt-0.5">{error.message}</p>
                  </div>
                  {error.replacements?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {error.replacements.slice(0, 2).map((replacement, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => applySuggestion(error, replacement.value)}
                          className="px-2 py-0.5 text-xs font-medium bg-white rounded border hover:bg-white/60 transition-colors"
                        >
                          {replacement.value}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {grammarErrors.length > 5 && (
              <p className="text-xs text-neutral-500 text-center">
                +{grammarErrors.length - 5} more suggestions
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Simple spellcheck-enabled textarea without the grammar button
 * Use this for smaller fields where grammar check is overkill
 */
export function SimpleSpellCheckTextarea({
  value,
  onChange,
  placeholder,
  rows = 3,
  className = '',
  ...props
}) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      spellCheck={true}
      autoCorrect="on"
      autoCapitalize="sentences"
      className={`w-full px-4 py-3 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${className}`}
      {...props}
    />
  );
}

/**
 * Spellcheck-enabled input for single-line text
 */
export function SpellCheckInput({
  value,
  onChange,
  placeholder,
  className = '',
  ...props
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      spellCheck={true}
      autoCorrect="on"
      autoCapitalize="sentences"
      className={`w-full px-4 py-2 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 ${className}`}
      {...props}
    />
  );
}

export default SpellCheckTextarea;
